import React, { useEffect, useMemo } from 'react';
import ArrowLeftIcon from '../components/Icons/ArrowLeftIcon';
import { useNavigate } from 'react-router-dom';
import LieferbarCard from '../components/LieferbarCard';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { useTranslation } from 'react-i18next';
import { getLieferbarOrders } from '../store/slices/lieferbarSlice';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { useLicenseCheck } from '../hooks/useLicenseCheck';
import { useAccountFeatures } from '../hooks/useLicenseCheck';
import { usePermissions } from '../hooks/usePermissions';
import UpgradePromoBlock from '../components/UpgradePromoBlock';
import WidgetUpgradeCTA from '../components/WidgetUpgradeCTA';

const LieferbarPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const profile = useSelector((state: RootState) => state.profile);
  const hasMobileLicense = useLicenseCheck('mobile');
  const { showLieferbar } = useAccountFeatures();
  // Subuser: the Lieferbar widget (open Wawi orders) requires its own key.
  const { can } = usePermissions();
  const canReadOrders = can('mobile_orders_read');

  const [firstIn, setFirstIn] = React.useState<boolean>(true);
  const { totalCount, orders, isConnected, upgradeRequired } = useAppSelector((state) => state.lieferbar);

  // The Lieferbar widget is gated by the mobile plan's `shipping_orders_enabled`
  // flag. `featureEnabled` gates the fetch (so a recovery fetch after an upgrade
  // can clear a stale `upgradeRequired` flag); `showWidget` additionally hides
  // the widget while the backend's upgrade-required 403 safety net is active.
  const featureEnabled = hasMobileLicense && showLieferbar && canReadOrders;
  const showWidget = featureEnabled && !upgradeRequired;
  const showUpgradeCta = hasMobileLicense && canReadOrders && (!showLieferbar || upgradeRequired);

  const snapshotDate = useMemo(() => {
    return new Intl.DateTimeFormat(i18n.language, {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    }).format(new Date());
  }, [i18n.language]);

  const sortedOrders = useMemo(() => {
    return [...(orders ?? [])].sort((a, b) =>
      (a.shippingType ?? '').localeCompare(b.shippingType ?? '', undefined, { sensitivity: 'base' }),
    );
  }, [orders]);

  useEffect(() => {
    // Do not call the gated `/wawi/orders` endpoint when the feature is off.
    if (!featureEnabled) {
      return;
    }

    let fetchDataInterval: ReturnType<typeof setInterval> | undefined;

    if (!firstIn) {
      if (isConnected) {
        fetchDataInterval = setInterval(() => {
          dispatch(getLieferbarOrders());
        }, 40000);
      }
    } else {
      dispatch(getLieferbarOrders());
      setFirstIn(false);
    }

    return () => {
      if (fetchDataInterval) clearInterval(fetchDataInterval);
      setFirstIn(false);
    };
  }, [dispatch, firstIn, isConnected, featureEnabled]);

  return (
    <div className="lieferbar-page">
      <header className="lieferbar-page__header">
        <button type="button" className="lieferbar-page__back" onClick={() => navigate(-1)} aria-label={t('back_button')}>
          <ArrowLeftIcon />
        </button>
        {t('lieferbar_title')}
      </header>

      {profile && !profile.contact?.parentUserId && !hasMobileLicense && (
        <UpgradePromoBlock pluginName="mobile" />
      )}

      {!canReadOrders && (
        <div className="dashboard-empty-state">
          <p className="dashboard-empty-state-text">{t('subuser_no_permission_title')}</p>
          <p className="dashboard-empty-state-hint">{t('subuser_no_permission_description')}</p>
        </div>
      )}

      {showUpgradeCta && (
        <WidgetUpgradeCTA
          title={t('lieferbar_upgrade_cta_title')}
          description={t('lieferbar_upgrade_cta_description')}
        />
      )}

      {showWidget && (
        <div className="lieferbar-widget">
          <div className="lieferbar-widget__date-row">
            <span className="lieferbar-widget__date-label">{t('lieferbar_date_label')}</span>
            <span className="lieferbar-widget__date-value">{snapshotDate}</span>
          </div>

          <div className="lieferbar-widget__gesamt">
            <span className="lieferbar-widget__gesamt-label">{t('lieferbar_total_count')}:</span>
            <span className="lieferbar-widget__gesamt-value">{totalCount ?? 0}</span>
          </div>

          {sortedOrders.length > 0 && (
            <div className="lieferbar-widget__grid">
              {sortedOrders.map((order) => (
                <LieferbarCard
                  key={order.shippingType}
                  label={order.shippingType}
                  count={order.pendingCount ?? 0}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LieferbarPage;
